/**
 * Property details utility functions
 * Validation, date handling, image processing, and ID utilities
 */

import type { ImageData } from '@/types'

/** Validation function signature */
export type ValidationFunction = (value: string | null) => string | false

/** Custom properties added to images in UI */
export interface ImageCustomProperties {
  isMainImageable: boolean
  isDeletable: boolean
  isLowQuality: boolean
}

/** Image with UI custom properties */
export interface ImageWithCustomProperties extends ImageData {
  customProperties: ImageCustomProperties
}

/** Item with ID (generic) */
export interface ItemWithId {
  id: number | string
  [key: string]: unknown
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate value contains latin characters
 * @param value - Value to validate
 * @returns Error key or false if valid
 */
export const latinChars: ValidationFunction = value =>
  value && /[a-zA-Z0-9]/.test(value) === false ? 'error_latin_chars' : false

/**
 * Validate value has no special characters
 * @param value - Value to validate
 * @returns Error key or false if valid
 */
export const noSpecialChars: ValidationFunction = value => {
  const pattern = new RegExp('[?~[\\]%(){}]', 'g')
  return value && pattern.test(value) ? 'error_special_chars' : false
}

/**
 * Validate maximum 4 consecutive capitalized letters
 * @param value - Value to validate
 * @returns Warning key or false if valid
 */
export const maximal4CapitalizedLetters: ValidationFunction = value => {
  return value && /[A-Z]{5,}/g.test(value) ? 'capital_letter_alert' : false
}

/**
 * Validate required field
 * @param value - Value to validate
 * @returns Error key or false if valid
 */
export const required: ValidationFunction = value =>
  value === null || value.length === 0 ? 'error_field_required' : false

/**
 * Validate email address format
 * @param value - Email address to validate
 * @returns Error key or false if valid
 */
export const emailAddress: ValidationFunction = value => {
  const pattern =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return value && pattern.test(value) ? false : 'error_invalid_email'
}

/**
 * Validate URL length (7-150 characters)
 * @param value - URL to validate
 * @returns Error key or false if valid
 */
export const minLength: ValidationFunction = value =>
  value && (value.length < 7 || value.length > 150) ? 'error_url_value_length' : false

/**
 * Validate URL format
 * @param value - URL to validate
 * @returns Error key or false if valid
 */
export const url: ValidationFunction = value => {
  const pattern = new RegExp(
    '^' +
      '(?:(?:https?)://)?' +
      '(?:' +
      '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
      '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
      '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
      '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
      '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
      '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
      '|' +
      '(?:(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)' +
      '(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*' +
      '(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))' +
      ')' +
      '(?::\\d{2,5})?' +
      '(?:[/?#]\\S*)?' +
      '$',
    'i'
  )

  return value && !pattern.test(value) ? 'err_11' : false
}

/**
 * Run multiple validation functions on a value
 * @param value - Value to validate
 * @param fns - Array of validation functions
 * @returns First error found or undefined if valid
 */
export function validate(value: string | null, fns: ValidationFunction[] = []): string | undefined {
  for (let i = 0; i < fns.length; i++) {
    const fn = fns[i]
    const error = fn(value)
    if (error !== false && error.length > 0) {
      return error
    }
  }
  return undefined
}

// ============================================================================
// Default Values
// ============================================================================

/** Default cost configuration */
export interface DefaultCosts {
  amount: number
  currency: string
  timeUnit: string
  timeAmount: number
}

/**
 * Generate default cost configuration
 * @param currency - Currency code (default: EUR)
 * @param timeUnit - Time unit (default: hour)
 * @returns Default cost object
 */
export function defaultCosts(currency = 'EUR', timeUnit = 'hour'): DefaultCosts {
  return {
    amount: 1,
    currency,
    timeUnit,
    timeAmount: 1,
  }
}

// ============================================================================
// Image Context Types
// ============================================================================

const MAIN_IMAGE = 'MAIN_IMAGE'
const HOTELIER = 'HOTELIER'
const PARTNER = 'PARTNER'

export const CONTEXT_TYPES = {
  MAIN_IMAGE,
  HOTELIER,
  PARTNER,
} as const

export type ContextType = (typeof CONTEXT_TYPES)[keyof typeof CONTEXT_TYPES]

// ============================================================================
// Image Status
// ============================================================================

const ACTIVE = 'ACTIVE'
const UPLOADING = 'UPLOADING'
const POLLING = 'POLLING'

export const STATUS = {
  ACTIVE,
  UPLOADING,
  POLLING,
} as const

export type ImageStatus = (typeof STATUS)[keyof typeof STATUS]

// ============================================================================
// Image Utilities
// ============================================================================

/**
 * Prepare new images collection with custom properties
 * Filters out main image and adds metadata flags
 * @param items - Array of image items from API
 * @returns Processed images with custom properties
 */
export function prepareNewImagesCollection(items: ImageData[] = []): ImageWithCustomProperties[] {
  const mainImage = items.find(item => item.isMainImage)
  const mainImageId = mainImage?.imageId

  const filteredItems = items.filter(element => element.imageId !== mainImageId)

  return filteredItems.map(item => ({
    ...item,
    customProperties: {
      isMainImageable: true,
      isDeletable: true,
      isLowQuality: item.width < 700 || item.height < 700,
    },
  }))
}

/**
 * Get main image from collection
 * @param items - Array of image items
 * @returns Array containing only main image
 */
export function getNewMainImage(items: ImageData[] = []): ImageData[] {
  return items.filter(item => item.isMainImage)
}

// ============================================================================
// ID Utilities
// ============================================================================

/**
 * Generate RFC4122 version 4 compliant GUID
 * @returns Generated GUID string
 */
export function generateGUID(): string {
  let d = new Date().getTime()
  let d2 = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16

    if (d > 0) {
      r = ((d + r) % 16) | 0
      d = Math.floor(d / 16)
    } else {
      r = ((d2 + r) % 16) | 0
      d2 = Math.floor(d2 / 16)
    }

    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/**
 * Filter items by array of IDs
 * @param ids - Array of IDs to match
 * @param items - Array of items with id property
 * @returns Filtered items matching provided IDs
 */
export function getItemsByIds<T extends ItemWithId>(ids: (number | string)[] = [], items: T[] = []): T[] {
  return items.filter(item => ids.includes(item.id))
}
